import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Cursor = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  background-color: #ffffff;
  border-radius: 50%;
  pointer-events: none;
  z-index: 9999;
  transition: transform 0.1s ease-out;
  mix-blend-mode: difference;
  border: 2px solid rgba(255, 255, 255, 0.5);

  &.hovered {
    transform: scale(1.5);
    background-color: #cccccc;
    border-color: rgba(255, 255, 255, 0.8);
  }

  &.clicked {
    transform: scale(0.8);
    background-color: #999999;
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const Bubble = styled.div`
  position: fixed;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  pointer-events: none;
  z-index: 9998;
  animation: bubbleFloat 2s ease-out forwards;

  @keyframes bubbleFloat {
    0% {
      transform: scale(1) translateY(0);
      opacity: 0.6;
    }
    100% {
      transform: scale(0) translateY(-50px);
      opacity: 0;
    }
  }
`;

const CustomCursor = () => {
  const cursorRef = useRef(null);
  const [bubbles, setBubbles] = useState([]);

  const createBubble = (x, y, isHover = false) => {
    const bubbleId = Date.now() + Math.random();
    const size = isHover ? Math.random() * 15 + 10 : Math.random() * 8 + 4;

    // Generate random rainbow color
    const hue = Math.random() * 360;
    const color = `hsl(${hue}, 70%, 60%)`;

    const newBubble = {
      id: bubbleId,
      x,
      y,
      size,
      color,
    };

    setBubbles(prev => [...prev, newBubble]);

    // Remove bubble after animation completes
    setTimeout(() => {
      setBubbles(prev => prev.filter(bubble => bubble.id !== bubbleId));
    }, 2000);
  };

  useEffect(() => {
    let mouseX = 0;
    let mouseY = 0;
    let cursorX = 0;
    let cursorY = 0;
    let lastBubbleTime = 0;

    const updateCursorPosition = () => {
      if (cursorRef.current) {
        cursorX += (mouseX - cursorX) * 0.1;
        cursorY += (mouseY - cursorY) * 0.1;

        cursorRef.current.style.left = `${cursorX - 10}px`;
        cursorRef.current.style.top = `${cursorY - 10}px`;
      }

      requestAnimationFrame(updateCursorPosition);
    };

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      // Create bubbles occasionally while moving
      const currentTime = Date.now();
      if (currentTime - lastBubbleTime > 100) { // Every 100ms
        createBubble(mouseX, mouseY, false);
        lastBubbleTime = currentTime;
      }
    };

    const handleMouseEnter = () => {
      if (cursorRef.current) {
        cursorRef.current.classList.add('hovered');
        // Create multiple bubbles when hovering
        for (let i = 0; i < 5; i++) {
          const x = mouseX;
          const y = mouseY;
          setTimeout(() => {
            createBubble(x, y, true);
          }, i * 80);
        }
      }
    };

    const handleMouseLeave = () => {
      if (cursorRef.current) {
        cursorRef.current.classList.remove('hovered');
      }
    };

    const handleMouseDown = () => {
      if (cursorRef.current) {
        cursorRef.current.classList.add('clicked');
        // Create burst of bubbles on click
        for (let i = 0; i < 8; i++) {
          const x = mouseX + (Math.random() - 0.5) * 20;
          const y = mouseY + (Math.random() - 0.5) * 20;
          setTimeout(() => {
            createBubble(x, y, true);
          }, i * 40);
        }
      }
    };

    const handleMouseUp = () => {
      if (cursorRef.current) {
        cursorRef.current.classList.remove('clicked');
      }
    };

    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      mouseX = touch.clientX;
      mouseY = touch.clientY;

      // Create bubbles occasionally while touching and moving
      const currentTime = Date.now();
      if (currentTime - lastBubbleTime > 100) { // Every 100ms
        createBubble(mouseX, mouseY, false);
        lastBubbleTime = currentTime;
      }
    };

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      mouseX = touch.clientX;
      mouseY = touch.clientY;

      if (cursorRef.current) {
        cursorRef.current.classList.add('clicked');
        // Create burst of bubbles on touch start
        for (let i = 0; i < 8; i++) {
          const x = mouseX + (Math.random() - 0.5) * 20;
          const y = mouseY + (Math.random() - 0.5) * 20;
          setTimeout(() => {
            createBubble(x, y, true);
          }, i * 40);
        }
      }
    };

    const handleTouchEnd = () => {
      if (cursorRef.current) {
        cursorRef.current.classList.remove('clicked');
      }
    };

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    // Add hover effects for interactive elements
    const interactiveElements = document.querySelectorAll('a, button, .btn, [role="button"]');
    interactiveElements.forEach(el => {
      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('mouseleave', handleMouseLeave);
    });

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    updateCursorPosition();

    // Continuous bubbling every 500ms even when not moving
    const bubbleInterval = setInterval(() => {
      createBubble(mouseX, mouseY, false);
    }, 500);

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      clearInterval(bubbleInterval);

      interactiveElements.forEach(el => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, []);

  return (
    <>
      <Cursor ref={cursorRef} />
      {bubbles.map(bubble => (
        <Bubble
          key={bubble.id}
          size={bubble.size}
          style={{
            left: `${bubble.x - bubble.size / 2}px`,
            top: `${bubble.y - bubble.size / 2}px`,
            backgroundColor: bubble.color,
          }}
        />
      ))}
    </>
  );
};

export default CustomCursor;
